import { Fragment, useLayoutEffect, useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Menu, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { EllipsisHorizontalIcon } from '@heroicons/react/20/solid';
import cn from './cn.js';

/**
 * Dropdown menu rendered through a portal so it is never clipped by
 * overflow-hidden tables/cards.
 *
 * items: Array<Item | Item[]> where nested arrays become separated groups.
 * `ariaLabel` names a custom trigger; `disabled` disables the trigger.
 * Item: { label, icon, onClick, to, href, danger, disabled, description, hidden }
 */
export default function Dropdown({
  button,
  items = [],
  align = 'right',
  width = 'w-52',
  label = 'Open menu',
  ariaLabel,
  className,
  buttonClassName,
  header,
  disabled = false,
}) {
  const buttonRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuWidth = parseInt(width.replace(/\D/g, ''), 10) * 4 || 208; // tailwind w-52 => 13rem
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 260 && rect.top > 260;
    const left = align === 'right' ? Math.max(8, rect.right - menuWidth) : Math.min(rect.left, window.innerWidth - menuWidth - 8);
    setPos({
      top: openUp ? undefined : rect.bottom + 6,
      bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
      left,
    });
  }, [align, width]);

  const groups = (Array.isArray(items[0]) ? items : [items]).map((group) => group.filter((item) => item && !item.hidden)).filter((g) => g.length);

  return (
    <Menu as="div" className={cn('relative inline-block text-left', className)}>
      {({ open }) => (
        <>
          <Menu.Button
            ref={buttonRef}
            onClick={updatePosition}
            className={cn(
              !button && 'inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
              buttonClassName,
            )}
            aria-label={ariaLabel || (button ? undefined : label)}
            disabled={disabled}
          >
            {button || <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />}
          </Menu.Button>
          <PortalMenu open={open} pos={pos} updatePosition={updatePosition} width={width} groups={groups} header={header} />
        </>
      )}
    </Menu>
  );
}

function PortalMenu({ open, pos, updatePosition, width, groups, header }) {
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updatePosition]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <Transition
      as={Fragment}
      show={open}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <Menu.Items
        static
        style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left }}
        className={cn('z-[90] origin-top-right rounded-xl bg-white p-1 shadow-pop ring-1 ring-gray-900/5 focus:outline-none', width)}
      >
        {header && <div className="px-2.5 py-2 text-xs text-gray-500">{header}</div>}
        {groups.map((group, gi) => (
          <div key={gi} className={cn(gi > 0 && 'mt-1 border-t border-gray-100 pt-1')}>
            {group.map((item, ii) => (
              <Menu.Item key={`${gi}-${ii}`} disabled={item.disabled}>
                {({ active }) => <DropdownItem item={item} active={active} />}
              </Menu.Item>
            ))}
          </div>
        ))}
      </Menu.Items>
    </Transition>,
    document.body,
  );
}

function DropdownItem({ item, active }) {
  const Icon = item.icon;
  const classes = cn(
    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
    item.disabled && 'cursor-not-allowed opacity-50',
    item.danger ? (active ? 'bg-red-50 text-red-700' : 'text-red-600') : active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
  );
  const content = (
    <>
      {Icon && <Icon className={cn('h-4 w-4 shrink-0', item.danger ? 'text-red-500' : 'text-gray-400')} aria-hidden="true" />}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{item.label}</span>
        {item.description && <span className="block truncate text-xs text-gray-500">{item.description}</span>}
      </span>
      {item.shortcut && <span className="ml-auto text-xs text-gray-400">{item.shortcut}</span>}
    </>
  );

  if (item.to && !item.disabled) {
    return (
      <Link to={item.to} className={classes}>
        {content}
      </Link>
    );
  }
  if (item.href && !item.disabled) {
    return (
      <a href={item.href} target={item.target} rel={item.target === '_blank' ? 'noopener noreferrer' : undefined} className={classes}>
        {content}
      </a>
    );
  }
  return (
    <button type="button" onClick={item.onClick} disabled={item.disabled} className={classes}>
      {content}
    </button>
  );
}
